<template>
  <div>
    <v-navigation-drawer 
      app
      permanent
      clipped
      color="secondary"
      width="300px"
      class="pt-6"
      v-bind="$attrs"
    >
      <div class="px-5">
        <!-- Time and Date -->
        <v-row>
          <!-- Date -->
          <v-col
            cols="6"
            class="secondary--text text--darken-4 font-weight-bold">
            {{ currentDate }}, {{ currentDay }}
          </v-col>
          <!-- Time -->
          <v-col
            cols="6"
            class="primary--text text-right font-weight-bold">
            {{ currentTime }}
          </v-col>
        </v-row>
        <!-- create case button -->
        <div
          class="align-items-center clickable my-3"
          @click="create.show = true"
        >
          <v-icon x-small color="primary">mdi-circle</v-icon>
          <span class="font-weight-bold primary--text">
            Create Case from PMS
          </span>
        </div>
        <!-- search field -->
        <v-text-field
          v-model="filter"
          dense
          rounded
          hide-details
          prepend-inner-icon="mdi-magnify"
          placeholder="Search"
          class="white py-2 my-3"
        ></v-text-field>
      </div>
      <!-- case list -->
      <div class="px-5 overflow-y" style="height:62vh">
        <!-- previous page -->
        <v-row justify="center">
          <v-btn
            v-if="page !== 1 && (sortedCases.length || filteredCases.length)"
            icon
            @click="prevPage"
            :loading="loadingPreviousCases"
            :disabled="loadingPreviousCases || loadingNextCases"
          >
            <v-icon x-large>mdi-chevron-up</v-icon>
          </v-btn>
        </v-row>

        <sidebar-cases
          v-for="(caseInfo, index) in filter ? filteredCases : sortedCases "
          :key="caseInfo.id"
          :case-info="caseInfo"
          @view="showDetailPopup(index)"
          @assign="showAssignPopup(index)"
        ></sidebar-cases>

        <v-row
          v-if="sortedCases.length == 0 && filteredCases.length == 0"
          justify="center"
          align="center"
          style="height: 100%;"
        >
          <h3 class="secondary--text text--darken-3">No cases for today</h3>
        </v-row>

        <!-- next page -->
        <v-row justify="center">
          <v-btn
            v-if="page !== lastPage && (sortedCases.length || filteredCases.length)"
            icon
            @click="nextPage"
            :loading="loadingNextCases"
            :disabled="loadingNextCases || loadingPreviousCases"
          >
            <v-icon x-large>mdi-chevron-down</v-icon>
          </v-btn>
        </v-row>
      </div>
    </v-navigation-drawer>

    <!-- case detail popup -->
    <v-dialog v-model="detail.show" max-width="800px">
      <sidebar-case-information
        :value="sortedCases[detail.index]"
        show-close-btn
        @close="hideDetailPopup"
      ></sidebar-case-information>
    </v-dialog>

    <!-- assign case popup -->
    <v-dialog v-model="assign.show" max-width="800px">
      <assign-case
        v-if="assign.index >= 0"
        :info="sortedCases[assign.index]"
        show-close-btn
        @close="hideAssignPopup"
      ></assign-case>
    </v-dialog>

    <!-- create case dialog -->
    <v-dialog v-model="create.show" max-width="900px" persistent>
      <create-case
        show-close-btn
        @close="create.close = true"
        :reset="!create.close"
        @showAssignCaseDialog="showAssignCaseDialog($event)"
      ></create-case>
    </v-dialog>

    <!-- confirmation popup -->
    <base-confirmation-dialog
      v-model="create.close"
      @cancel="create.close = false"
      @confirm="closeCreateDialog"
    >
      <h2
        class="font-weight-regular text-center"
        style="line-height: 130%;"
      >
        Are you sure you want to cancel creating new case? All inputs will not be saved.
      </h2>
    </base-confirmation-dialog>
  </div>
</template>

<script>
  import Pusher from 'pusher-js'
  import moment from 'moment'
  // import API from '@/api';
  // const Cases = API.get('Cases');

  export default {
    name: 'Sidebar',

    components: {
      SidebarCases: () => import('./SidebarCases.vue'),
      SidebarCaseInformation: () => import('./SidebarCaseInformation.vue'),
      CreateCase: () => import('./CreateCase.vue'),
      AssignCase: () => import('./AssignCase.vue'),
    },

    data() {
      return {
        loadingPreviousCases: false,
        loadingNextCases: false,
        page: 1,
        sortedPage: 1,
        create: {
          show: false,
          close: false
        },
        filter: '',
        now: {
          intervalId : null,
          dateTime: new Date(),
        },
        detail: {
          show: false,
          index: null
        },
        assign: {
          show: false,
          index: null
        },
        pusher: {
          key: process.env.VUE_APP_PUSHER_KEY,
          cluster: process.env.VUE_APP_PUSHER_CLUSTER,
          secret: process.env.VUE_APP_PUSHER_SECRET,
          appId: process.env.VUE_APP_PUSHER_ID,
        }
      }
    },

    watch: {
      async sortedCases(val) {
        if (!val.length) {
          if (this.lastPage !== this.sortedPage) {
            this.sortedPage++
            await this.$store.dispatch('cases/fetchCasesConsultation', {no_doctor: true, page: this.sortedPage});
          }
        }
        else {
          console.log('[sorted-cases]', val)
          // console.log(JSON.stringify(val[0]));
        }
      }
    },

    computed: {
      lastPage() { return this.$store.getters['cases/last_page'] },
      cases() {
        return this.$store.getters['cases/cases']
      },
      // today onwards cases
      sortedCases() {
        return this.cases.filter(item => {
          return moment(item.preferred_datetime).isSameOrAfter(moment())
        })
      },
      currentDate() {
        const config = { month: 'short', day: 'numeric' };
        return this.now.dateTime.toLocaleString('default', config);
      },
      currentDay() {
        const config = { weekday: 'short' };
        return this.now.dateTime.toLocaleString('default', config);
      },
      currentTime() {
        const config = { hour: '2-digit', minute: '2-digit' };
        return this.now.dateTime.toLocaleString('default', config);
      },
      filteredCases() {
        return this.sortedCases.filter(item => {
          var filter = this.filter.toLowerCase()
          return (
               item.patient.email.toLowerCase().includes(filter)
            || item.patient.patient.first_name.toLowerCase().includes(filter)
            || item.patient.patient.last_name.toLowerCase().includes(filter)
            || item.patient.patient.full_name.toLowerCase().includes(filter)
            || item.patient.patient.full_name.toLowerCase().includes(filter)
            || item.health_concern.toLowerCase().includes(filter)
            || item.case_number.toLowerCase().includes(filter)
          )
        })
      }
    },

    methods: {
      async nextPage() {
        this.loadingNextCases = true
        await this.page++
        await this.$store.dispatch('cases/fetchCasesConsultation', {no_doctor: true, page: this.page});
        this.loadingNextCases = false
      },
      async prevPage() {
        this.loadingPreviousCases = true
        await this.page--
        await this.$store.dispatch('cases/fetchCasesConsultation', {no_doctor: true, page: this.page});
        this.loadingPreviousCases = false
      },
      /** create case methods */
      closeCreateDialog() {
        this.resetForm
        console.log( this.resetForm, "resetfomr");
        this.create.close = false
        this.create.show = false
      },

      /** real time notification/listener methods */
      async newCaseListener() {
        var me = this;
        var pusher = new Pusher(this.pusher.key, {
          cluster: this.pusher.cluster
        });

        const channel = pusher.subscribe('patient-case');

        // channel.bind('App\\Events\\CaseConsultationCreated', function(data) {
        //   console.log('[CaseConsultationCreated]', data);
        // });

        channel.bind('App\\Events\\PatientCaseCreated', async function(data) {
          console.log('[PatientCaseCreated]', data);
          await me.$store.commit('cases/pushNewCase', {data: data.patientCase, index: null});
        });
      },

      receiveTransferredCase() {
        // var me = this;
        var pusher = new Pusher(this.pusher.key, {
          cluster: this.pusher.cluster
        });

        const channel = pusher.subscribe('consultation');

        channel.bind('updated', async function(data) {
          console.log('[PatientCaseTransferred]', data);

          // let fetchCase = await Cases.get(data.id);

          // console.log(me.filteredCases, "filteredCases")

          // console.log({ data: fetchCase.data });
          // await me.filteredCases.push(fetchCase.data);

          // await me.$store.commit('cases/pushNewCase', {data: fetchCase.data, index: null});
          // await me.$store.dispatch('consultation/fetchConsultation', {id: data.patientCase.consultations[0].id, patient: data.patientCase.patient, no_info: true});
        });
      },

      transferCaseFromDoctor() {
        var me = this;

        console.log("transferCaseFromDoctor")
        var pusher = new Pusher(this.pusher.key, {
          cluster: this.pusher.cluster
        });

        const channel = pusher.subscribe('patient-case');

        channel.bind('updated', async function(data) {
          console.log({ data }, "transferCaseFromDoctor")

          let haveAssignedDoctor = data.patientCase.doctor_id ? true : false;

          let caseIdAlreadyExistsInItems = me.$store.getters['cases/cases'].some(item => item.id == data.patientCase.id);

          if (!haveAssignedDoctor && !caseIdAlreadyExistsInItems) {
            await me.$store.commit('cases/pushNewCase', {data: data.patientCase, index: null});
          }

        });
      },

      showDetailPopup(index) {
        this.detail.show = true
        this.detail.index = index
      },
      hideDetailPopup() {
        this.detail.show = false
        this.detail.index = null
      },

      /**
       * Assign Case dialogs
       */
      showAssignPopup(index) {
        this.assign.show = true
        this.assign.index = index
      },
      hideAssignPopup() {
        this.assign.show = false
        this.assign.index = null
      },
      async showAssignCaseDialog(event) {
        console.log({ event, filteredCases: this.filteredCases });

        let findFilteredCasesIndex = await this.filteredCases.findIndex(item => item.id == event.id);

        console.log({ findFilteredCasesIndex });

        if (findFilteredCasesIndex > -1) {
          await this.showAssignPopup(findFilteredCasesIndex);
        } else {
          await this.filteredCases.push(event);

          findFilteredCasesIndex = await this.filteredCases.findIndex(item => item.id == event.id);
          await this.showAssignPopup(findFilteredCasesIndex);
        }

        this.create.show = false;
        // this.create.close = true;
      },
      
      
      /** HELPERS */
      consolelog(nI) {
        console.log(nI)
      },
    },

    async beforeCreate () {
      await this.$store.dispatch('cases/fetchCasesConsultation', {no_doctor: true, page: this.page});
    },
    mounted() {
      // updates time every 1 minute
      this.now.intervalId = setInterval(()=>{
        this.now.dateTime = new Date()
      },60000);

      // listen for new callback requests
      this.newCaseListener();
      this.receiveTransferredCase();
      this.transferCaseFromDoctor();

      console.log('[cases]', this.sortedCases, this.filteredCases)
    },

    beforeDestroy() {
      // clears interval to prevent leak and unreachable interval stacks
      clearInterval(this.now.intervalId);
    }
  }
</script>

<style scoped>
</style>